import React, { useState } from 'react';
import { useLocation } from '../contexts/LocationContext';
import { useWarehouse } from '../contexts/WarehouseContext';
import { usePermissions } from '../contexts/PermissionsContext';
import InvoiceCorrectionModal from '../components/InvoiceCorrectionModal';

import { SalesInvoiceList, CorrectionsTab, ReceiptToInvoice, SalesInvoiceForm, useSalesData } from '../components/sales';

const SalesInvoicesPage = () => {
  const { selectedLocation, locationId } = useLocation();
  const { selectedWarehouse, warehouseId } = useWarehouse();
  const { hasPermission } = usePermissions();

  // Uprawnienia faktur
  const canAccessInvoices = hasPermission('faktury', 'dostep', locationId);
  const canViewInvoices = hasPermission('faktury', 'podglad', locationId);
  const canCreateInvoices = hasPermission('faktury', 'wystawianie', locationId);
  const canCorrectInvoices = hasPermission('faktury', 'korekty', locationId);

  // Uzyj location_id dla adminow, warehouse_id dla pracownikow
  const currentLocationId = locationId || warehouseId;

  const [activeTab, setActiveTab] = useState('invoices');

  // Custom hook for all sales data and actions
  const {
    invoices,
    receipts,
    totalReceipts,
    customers,
    loading,
    error,
    setError,

    // KSeF
    ksefEnabled,
    ksefSending,
    sendToKsef,
    downloadUpo,

    // Invoice actions
    previewInvoice,
    openCorrectionModal,
    handleCorrectionComplete,
    showCorrectionModal,
    selectedInvoiceForCorrection,
    closeCorrectionModal,

    // Receipts
    receiptsLimit,
    currentPage,
    filterDate,
    filterDateValue,
    setFilterDateValue,
    handleFilterChange,
    handleReceiptsLimitChange,
    goToPage,
    fetchReceiptDetails,
    createInvoiceFromReceipt,

    // Modal
    showCreateModal,
    selectedReceipt,
    receiptEligibility,
    closeCreateModal,
  } = useSalesData({ currentLocationId });

  if (loading) {
    return (
      <div className="container-fluid mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Ladowanie...</span>
          </div>
        </div>
      </div>
    );
  }

  // Sprawdz czy uzytkownik ma dostep do modulu faktur
  if (!canAccessInvoices && !canViewInvoices) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        backgroundColor: 'var(--bg)',
        minHeight: '100vh'
      }}>
        <div style={{
          backgroundColor: 'var(--warning-light)',
          color: 'var(--warning)',
          padding: '1.5rem',
          borderRadius: '8px',
          border: '1px solid var(--warning)',
          maxWidth: '500px',
          margin: '2rem auto'
        }}>
          <h5 style={{ marginBottom: '0.5rem' }}>🔒 Brak dostepu</h5>
          <p style={{ margin: 0, fontSize: '13px' }}>
            Nie masz uprawnien do modulu Faktury. Skontaktuj sie z administratorem.
          </p>
        </div>
      </div>
    );
  }

  // Count corrections and regular invoices for tab badges
  const correctionsCount = invoices.filter(inv => inv.typ_faktury === 'korekta').length;
  const regularInvoicesCount = invoices.filter(inv => inv.typ_faktury !== 'korekta').length;

  return (
    <div>
      {/* Naglowek */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>Faktury Sprzedazy</h2>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Zarzadzanie fakturami sprzedazy</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
          <button
            type="button"
            className="btn-close float-end"
            onClick={() => setError('')}
          ></button>
        </div>
      )}

      {/* Tabs - styl POS */}
      <div style={{
        display: 'flex',
        gap: '0.25rem',
        marginBottom: '0.75rem',
        backgroundColor: 'var(--card-bg)',
        padding: '0.5rem',
        borderRadius: '4px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
      }}>
        <TabButton
          active={activeTab === 'invoices'}
          onClick={() => setActiveTab('invoices')}
          icon={'📄'}
          label={`Faktury (${regularInvoicesCount})`}
        />
        <TabButton
          active={activeTab === 'corrections'}
          onClick={() => setActiveTab('corrections')}
          icon={'📝'}
          label={`Korekty (${correctionsCount})`}
        />
        <TabButton
          active={activeTab === 'receipts'}
          onClick={() => setActiveTab('receipts')}
          icon={'🧾'}
          label={`Paragony do fakturowania (${totalReceipts})`}
        />
      </div>

      {/* Tab content */}
      {activeTab === 'invoices' && (
        <SalesInvoiceList
          invoices={invoices}
          ksefEnabled={ksefEnabled}
          ksefSending={ksefSending}
          canCorrectInvoices={canCorrectInvoices}
          onPreview={previewInvoice}
          onCorrection={openCorrectionModal}
          onSendToKsef={sendToKsef}
          onDownloadUpo={downloadUpo}
          setError={setError}
        />
      )}

      {activeTab === 'corrections' && (
        <CorrectionsTab
          invoices={invoices}
          ksefEnabled={ksefEnabled}
          ksefSending={ksefSending}
          onSendToKsef={sendToKsef}
          onDownloadUpo={downloadUpo}
          setError={setError}
        />
      )}

      {activeTab === 'receipts' && (
        <ReceiptToInvoice
          receipts={receipts}
          totalReceipts={totalReceipts}
          receiptsLimit={receiptsLimit}
          currentPage={currentPage}
          filterDate={filterDate}
          filterDateValue={filterDateValue}
          setFilterDateValue={setFilterDateValue}
          onFilterChange={handleFilterChange}
          onLimitChange={handleReceiptsLimitChange}
          onPageChange={goToPage}
          onCreateInvoice={(receiptId) => fetchReceiptDetails(receiptId, canCreateInvoices)}
        />
      )}

      {/* Modal tworzenia faktury */}
      {showCreateModal && selectedReceipt && (
        <SalesInvoiceForm
          receipt={selectedReceipt}
          customers={customers}
          eligibility={receiptEligibility}
          currentLocationId={currentLocationId}
          onClose={closeCreateModal}
          onSubmit={createInvoiceFromReceipt}
        />
      )}

      {/* Modal korekty faktury */}
      <InvoiceCorrectionModal
        isOpen={showCorrectionModal}
        onClose={closeCorrectionModal}
        invoice={selectedInvoiceForCorrection}
        onCorrectionComplete={handleCorrectionComplete}
      />
    </div>
  );
};

// Tab button subcomponent
const TabButton = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    style={{
      padding: '6px 12px',
      fontSize: '12px',
      backgroundColor: active ? 'var(--primary)' : 'var(--card-bg)',
      border: active ? '1px solid var(--primary)' : '1px solid var(--border)',
      color: active ? '#fff' : 'var(--text-secondary)',
      cursor: 'pointer',
      fontWeight: active ? '600' : '500',
      display: 'flex',
      alignItems: 'center',
      gap: '0.35rem'
    }}
  >
    {icon} {label}
  </button>
);

export default SalesInvoicesPage;
